import { useState, useRef } from "react";
import {
  Alert,
  Button,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  rem,
} from "@mantine/core";
import {
  IconUpload,
  IconFile,
  IconX,
  IconSend,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { SourceLanguageSelect, SingleTargetLanguageSelect } from "@features/LanguageSelect";
import { MODEL_OPTIONS } from "@models/batch";
import type { TextUnit } from "@models/batch";
import { parseFileToTextUnits } from "./fileParser";

const ACCEPTED_EXTENSIONS = ".csv,.tsv,.txt,.json,.jsonl";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

interface Props {
  onSubmit: (opts: {
    sourceLang: string;
    targetLang: string;
    model: string;
    textUnits: TextUnit[];
    fileName: string;
  }) => void;
  submitting: boolean;
  error: string | null;
}

export const BatchUploadPanel: React.FC<Props> = ({ onSubmit, submitting, error }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [textUnits, setTextUnits] = useState<TextUnit[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [sourceLang, setSourceLang] = useState("en-GB");
  const [targetLang, setTargetLang] = useState("ko-KR");
  const [model, setModel] = useState("gpt-4.1");

  const handleFileSelect = async (selectedFile: File | null) => {
    setParseError(null);
    setTextUnits([]);

    if (!selectedFile) {
      setFile(null);
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setParseError(t("batch.fileTooLarge"));
      return;
    }

    try {
      const units = await parseFileToTextUnits(selectedFile);
      if (units.length === 0) {
        setParseError(t("batch.noUnitsFound"));
        return;
      }
      setFile(selectedFile);
      setTextUnits(units);
    } catch (err) {
      setParseError(
        err instanceof Error ? err.message : t("batch.parseError"),
      );
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    void handleFileSelect(selected);
    // Reset input so re-selecting same file triggers change
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files?.[0] ?? null;
    void handleFileSelect(dropped);
  };

  const handleSubmit = () => {
    if (!file || textUnits.length === 0) return;
    onSubmit({
      sourceLang,
      targetLang,
      model,
      textUnits,
      fileName: file.name,
    });
  };

  const canSubmit = file !== null && textUnits.length > 0 && !submitting;

  return (
    <Stack gap="md">
      {/* File Upload Area */}
      <Paper
        withBorder
        radius="md"
        p="lg"
        style={{
          borderStyle: "dashed",
          borderWidth: "2px",
          borderColor: "var(--mantine-color-gray-5)",
          cursor: "pointer",
          textAlign: "center",
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label={t("batch.dropzone")}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          style={{ display: "none" }}
          onChange={handleFileInputChange}
        />

        {file ? (
          <Group justify="center" gap="sm">
            <IconFile size={22} style={{ color: "var(--mantine-color-brand-6)" }} />
            <div>
              <Text fw={500} size="sm">{file.name}</Text>
              <Text size="xs" c="dimmed">
                {textUnits.length} {t("batch.unitsDetected")}
              </Text>
            </div>
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              aria-label={t("translation.clear")}
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
                setTextUnits([]);
              }}
            >
              <IconX size={14} />
            </Button>
          </Group>
        ) : (
          <Stack align="center" gap={4}>
            <IconUpload size={32} style={{ opacity: 0.4 }} />
            <Text fw={500} size="sm">{t("batch.dropzone")}</Text>
            <Text size="xs" c="dimmed">
              {t("batch.acceptedFormats")}
            </Text>
          </Stack>
        )}
      </Paper>

      {parseError && (
        <Alert color="red" icon={<IconX size={16} />}>
          {parseError}
        </Alert>
      )}

      {/* Configuration */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm" style={{ alignItems: "flex-end" }}>
        <SourceLanguageSelect
          value={sourceLang}
          onChange={setSourceLang}
          label={t("translation.source")}
          disabled={submitting}
        />
        <SingleTargetLanguageSelect
          value={targetLang}
          onChange={setTargetLang}
          label={t("batch.targetLang")}
          disabled={submitting}
        />
        <Select
          label={t("batch.model")}
          data={MODEL_OPTIONS.map((m) => ({ value: m.value, label: m.label }))}
          value={model}
          onChange={(v) => setModel(v ?? "gpt-4.1")}
          disabled={submitting}
          allowDeselect={false}
        />
      </SimpleGrid>

      {error && <Alert color="red">{error}</Alert>}

      {/* Submit */}
      <Group justify="flex-end">
        <Button
          leftSection={<IconSend size={rem(16)} />}
          onClick={handleSubmit}
          loading={submitting}
          disabled={!canSubmit}
          color="brand"
          variant="filled"
        >
          {t("batch.submit")}
        </Button>
      </Group>
    </Stack>
  );
};
